import React, { useEffect, useState } from "react";
import {
  always,
  applySpec,
  ascend,
  descend,
  map,
  pipe,
  prop,
  sortWith,
} from "ramda";
import {
  getConversationMetadata,
  getConversationRequestTime,
  getConversationStatus,
  getLastAgentMessageTime,
  getLastUserMessageTime,
  statusTypes,
} from "./conversationHistorytools";
import CustomButton from "./CustomButton";
import { RiRestTimeLine } from "react-icons/ri";
import VerificationButtons from "./VerificationButtons";
import { createUseStyles } from "react-jss";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";

const STATUS_TO_COLOR = {
  "Pending your reply": "#ff6262",
  "In progress": "#01998C",
  ended: "black",
  verification: "#151647",
};

const CONVERSATION_TYPES = {
  INCOMING: "incoming",
  ACTIVE: "active",
};

const useStyles = createUseStyles({
  section: {
    padding: "20px 20px 0px 20px",
  },
  headline: {
    fontSize: "18px",
    color: "#464775",
    marginBottom: "5px",
  },
  headlineConversationsCount: {
    fontSize: "11px",
    marginLeft: "3px",
  },
  conversationContainer: {
    backgroundColor: "white",
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "20px 25% 35% 30% 15%",
    gridTemplateRows: "[row1-start] 45px [row1-end] repeat(auto-fill, 60px)",
    marginRight: "10%",
    padding: "0 0 20px 20px",
    "& p": {
      color: "#151647",
      lineHeight: "24px",
      fontSize: "13px",
    },
    "& h4": {
      color: "#151647",
      lineHeight: "24px",
      fontSize: "11px",
    },
    height: "100%",
  },
  gridWrapper: {
    boxSizing: "border-box",
    height: "400px",
    overflowY: "auto",
  },
});

const materialUiStyles = makeStyles(() => ({
  acceptButton: {
    textTransform: "none",
    backgroundColor: "#01998C",
    color: "#fff",
    width: "fit-content",
    minWidth: "55px",
    minHeight: "29px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#017369", boxShadow: "none" },
  },
  endButton: {
    textTransform: "none",
    backgroundColor: "#464775",
    color: "#fff",
    width: "fit-content",
    minWidth: "55px",
    minHeight: "29px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#2E2E4D !important", boxShadow: "none" },
  },
}));

const conversationUseStyles = createUseStyles((theme) => ({
  rowWrapper: {
    display: "contents",

    "&:hover > div": {
      backgroundColor: "rgba(70, 71, 117, 0.06)",
    },
    "&:active > div": {
      backgroundColor: "rgba(70, 71, 117, 0.08)",
    },
    "& p": {
      margin: "2px 0",
      textOverflow: "ellipsis",
      display: "block",
      overflow: "hidden",
      whiteSpace: "nowrap",
      color: theme.conversationTextColor,
    },
  },

  selectedConversation: {
    "& > div": {
      backgroundColor: "rgba(70, 71, 117, 0.08)",
    },
  },
  conversation: {
    minHeight: "60px",
    borderTop: `solid 1px ${theme.separatorGray}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingLeft: "5px",
  },
  conversationTitle: { margin: "0", color: "#151647 !important" },
  date: {
    borderTop: `solid 1px ${theme.separatorGray}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  status: {
    borderTop: `solid 1px ${theme.separatorGray}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    color: "#151647",
    textTransform: "capitalize",
  },
  action: {
    borderTop: `solid 1px ${theme.separatorGray}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  actions: {
    display: "inline-flex",
  },
}));

const makeConversationsArray = pipe(
  Object.entries,
  map(([conversationId, { conversationHistory }]) =>
    applySpec({
      conversationId: always(conversationId),
      conversationStatus: getConversationStatus,
      metadata: getConversationMetadata,
      requestTime: getConversationRequestTime,
      lastUserMessageTime: getLastUserMessageTime,
      lastAgentMessageTime: getLastAgentMessageTime,
    })(conversationHistory)
  ),
  sortWith([
    descend(prop("conversationStatus")),
    ascend(prop("lastUserMessageTime")),
  ])
);

const Conversation = ({
  conversation: {
    conversationId,
    conversationStatus,
    metadata,
    requestTime,
    lastUserMessageTime,
    lastAgentMessageTime,
  },
  selectedConversationId,
  onSelectConversation,
  onAcceptOrEndConversation,
  type,
}) => {
  const styles = conversationUseStyles();
  const materialStyles = materialUiStyles();
  const [agentWantToEndConvo, setAgentWantToEndConvo] = useState(false);
  const [isInactiveUser, setIsInactiveUser] = useState(
    !(
      conversationStatus[1] === statusTypes.PENDING_YOUR_REPLY ||
      conversationStatus[0] === statusTypes.ENDED
    )
  );
  const [userLastSeen, setUserLastSeen] = useState(0);
  const twoMinutes = 120000;

  useEffect(() => {
    const setInactiveUserUserLastSeen = () => {
      setIsInactiveUser(
        Date.now() - Date.parse(lastUserMessageTime) > twoMinutes &&
          Date.parse(lastAgentMessageTime) > Date.parse(lastUserMessageTime) &&
          !(
            conversationStatus[0] === statusTypes.ENDED ||
            conversationStatus[1] === statusTypes.PENDING_YOUR_REPLY
          )
      );
      setUserLastSeen(
        Math.floor((Date.now() - Date.parse(lastUserMessageTime)) / 60000)
      );
    };
    setInactiveUserUserLastSeen();
    const interval = setInterval(setInactiveUserUserLastSeen, 30000);
    return () => clearInterval(interval);
  }, [conversationStatus, lastAgentMessageTime, lastUserMessageTime]);

  return (
    <div
      className={[
        styles.rowWrapper,
        selectedConversationId && selectedConversationId === conversationId
          ? styles.selectedConversation
          : "",
      ].join(" ")}
      onClick={() => {
        if (type === CONVERSATION_TYPES.ACTIVE) {
          onSelectConversation(conversationId);
        }
      }}
    >
      <div />
      <div className={styles.conversation}>
        <p
          className={styles.conversationTitle}
          style={
            conversationStatus[0] === statusTypes.LIVE &&
            (conversationStatus[1] === statusTypes.PENDING_YOUR_REPLY ||
              type === CONVERSATION_TYPES.INCOMING)
              ? { fontWeight: "bold" }
              : { fontWeight: "normal" }
          }
        >
          {metadata.patientName}
        </p>
      </div>

      <div className={styles.date}>
        <p>
          {moment(
            type === CONVERSATION_TYPES.INCOMING
              ? requestTime
              : lastUserMessageTime
          ).format("llll")}
        </p>
      </div>
      <div className={styles.status}>
        {agentWantToEndConvo ? (
          <p style={{ color: STATUS_TO_COLOR["verification"] }}>
            End this conversation?
          </p>
        ) : (
          <>
            <p
              style={
                conversationStatus[0] === statusTypes.ENDED
                  ? {
                      color: STATUS_TO_COLOR[conversationStatus[0]],
                      lineHeight: "15px",
                    }
                  : {
                      color: STATUS_TO_COLOR[conversationStatus[1]],
                      lineHeight: "15px",
                    }
              }
            >
              {type === CONVERSATION_TYPES.INCOMING
                ? statusTypes.PENDING_AN_AGENT
                : conversationStatus[0] === statusTypes.ENDED
                ? statusTypes.ENDED
                : conversationStatus[1]}
            </p>
            {isInactiveUser && type === CONVERSATION_TYPES.ACTIVE && (
              <p style={{ lineHeight: "11px", fontSize: "11px " }}>
                <RiRestTimeLine /> user last seen - {userLastSeen} min
              </p>
            )}
          </>
        )}
      </div>
      <div className={styles.action}>
        {" "}
        <>
          {conversationStatus[0] === statusTypes.ENDED && <p>-</p>}
          {agentWantToEndConvo &&
            !(conversationStatus[0] === statusTypes.ENDED) && (
              <VerificationButtons
                onConfirm={() => {
                  setAgentWantToEndConvo(false);
                  onAcceptOrEndConversation(conversationId);
                }}
                onReject={() => setAgentWantToEndConvo(false)}
              />
            )}
          {type === CONVERSATION_TYPES.INCOMING && (
            <CustomButton
              text={"Accept"}
              customOnClick={() => onAcceptOrEndConversation(conversationId)}
              style={materialStyles.acceptButton}
            />
          )}
          {type === CONVERSATION_TYPES.ACTIVE &&
            !agentWantToEndConvo &&
            !(conversationStatus[0] === statusTypes.ENDED) && (
              <CustomButton
                text={"End"}
                customOnClick={() => setAgentWantToEndConvo(true)}
                style={materialStyles.endButton}
              />
            )}
        </>
      </div>
    </div>
  );
};

export default ({
  conversations,
  selectedConversationId,
  onSelectConversation,
  onAcceptOrEndConversation,
  type,
  agentOnlineStatus,
}) => {
  const styles = useStyles();
  return (
    <div
      className={styles.gridWrapper}
      style={type === CONVERSATION_TYPES.INCOMING ? { height: "350px" } : {}}
    >
      <div className={styles.section}>
        <h2 className={styles.headline}>
          {type === CONVERSATION_TYPES.INCOMING
            ? "Incoming"
            : "My Conversations"}
          <span className={styles.headlineConversationsCount}>
            ({Object.keys(conversations).length})
          </span>
        </h2>
      </div>
      <div className={styles.conversationContainer}>
        <div />
        <h4>Patient name</h4>
        <h4>
          {type === CONVERSATION_TYPES.INCOMING
            ? "Request time"
            : "Last user message"}
        </h4>
        <h4>Status</h4>
        <h4>Action</h4>
        {!(
          type === CONVERSATION_TYPES.INCOMING && agentOnlineStatus === false
        ) &&
          makeConversationsArray(conversations).map((conversation) => (
            <Conversation
              conversation={conversation}
              key={conversation.conversationId}
              onSelectConversation={onSelectConversation}
              selectedConversationId={selectedConversationId}
              onAcceptOrEndConversation={onAcceptOrEndConversation}
              type={type}
            />
          ))}
      </div>
    </div>
  );
};
